import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import moment from "moment";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import {
  TRANSACTION_EXPORT_MAX_RANGE_DAYS,
  fetchAllExportRows,
  saveExportFile,
  validateExportRange,
} from "./TransactionExport.service";

const today = () => moment().format("YYYY-MM-DD");

// Transaction export in the reconciliation layout (one row per transaction).
const TransactionExportDialog = ({
  open,
  onClose,
  merchantID,
  terminalID,
  isoID,
  token,
  acquirerName,
  acquirerID,
}) => {
  const dispatch = useDispatch();
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [format, setFormat] = useState("xlsx");
  const [loading, setLoading] = useState(false);
  const rangeError = validateExportRange(fromDate, toDate);

  const handleExport = async () => {
    if (rangeError) return;
    setLoading(true);
    try {
      const { columns, rows, truncated } = await fetchAllExportRows({
        fromDate,
        toDate,
        merchantID,
        terminalID,
        isoID,
        token,
        acquirerName,
        acquirerID,
      });
      if (!rows.length) {
        dispatch(
          showSnackbar({
            message: "No transactions in this range.",
            severity: "warning",
          }),
        );
        return;
      }
      saveExportFile({ columns, rows, format, fromDate, toDate });
      dispatch(
        showSnackbar({
          message: truncated
            ? `Exported ${rows.length} transactions; the range has more. Use a shorter range.`
            : `Exported ${rows.length} transactions.`,
          severity: truncated ? "warning" : "success",
        }),
      );
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      dispatch(
        showSnackbar({
          message:
            typeof detail === "string"
              ? `Export failed: ${detail}`
              : "Export failed. Try again.",
          severity: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Export transactions</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Reconciliation layout, one row per transaction, by creation date.
          {merchantID?.trim()
            ? ` Merchant ${merchantID.trim()}`
            : isoID?.trim()
              ? ` All merchants of ISO ${isoID.trim()}`
              : " All merchants"}
          {terminalID?.trim() ? `, terminal ${terminalID.trim()}.` : "."} Up to{" "}
          {TRANSACTION_EXPORT_MAX_RANGE_DAYS} days.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="From"
              type="date"
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today() }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="To"
              type="date"
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today() }}
              error={Boolean(rangeError)}
              helperText={rangeError || " "}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Format"
              fullWidth
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <MenuItem value="xlsx">XLSX</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={loading || Boolean(rangeError)}
        >
          {loading ? "EXPORTING..." : "EXPORT"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionExportDialog;
